import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MapLayerMouseEvent, GeoJSONSource, HeatmapPaint } from 'mapbox-gl';
import ReactMapGL, {
  Source,
  Layer,
  Popup,
  NavigationControl,
  GeolocateControl,
  FullscreenControl,
  ScaleControl,
  type MapRef,
} from 'react-map-gl';
import {
  AppBar,
  Autocomplete,
  Box,
  Button,
  Card,
  CardActionArea,
  Checkbox,
  CircularProgress,
  Dialog,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useFeatureFlagsStore } from '@signals/app-shell/stores/featureFlagsStore';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import LayersIcon from '@mui/icons-material/Layers';
import StorageIcon from '@mui/icons-material/Storage';
import PlaceIcon from '@mui/icons-material/Place';
import BubbleChartIcon from '@mui/icons-material/BubbleChart';
import GradientIcon from '@mui/icons-material/Gradient';
import TuneIcon from '@mui/icons-material/Tune';
import type { LayerDisplayMode, MapControlToggles, MapBookmark, MapAnnotation, WebMapLayerConfig, WebMapWidgetOptions } from '../types/webMap';
import {
  fetchMapLayerCatalog,
  fetchLayerGeoJson,
  fetchLayerColumns,
  inferColumnsFromFeatures,
  type MapLayerCatalogEntry,
  type ColumnInfo,
  type DbTarget,
} from '@signals/app-shell/services/mapLayersService';
import {
  fetchSourceCatalog,
  fetchSourceGeoJson,
  type SourceCatalogEntry,
} from './sourceCatalogService';
import { SettingsColorPicker, SettingsSlider, SettingsOpacitySlider } from '../builder/controls';
import MapboxBasemapStrip, { type BasemapOption } from './MapboxBasemapStrip';
import MapboxSpriteIconPicker from './MapboxSpriteIconPicker';
import {
  MAX_WEB_MAP_CATEGORIES,
  MAPBOX_STYLE_SPRITE_ICONS,
  categoryColorExpression,
  categoryIconExpression,
  deriveCategoryStyles,
  webMapGeoJsonSourceKey,
  resolveSavedSpriteIcon,
  heatmapWeightPaint,
} from './webMapMapboxUtils';
import { popupPropertyEntries } from './webMapPopupUtils';
import MapToolbar, { type MapTool } from './MapToolbar';
import MapMeasureOverlay, { type MeasurePoint } from './MapMeasureOverlay';
import MapLegendOverlay from './MapLegendOverlay';
import MapBookmarksPanel from './MapBookmarksPanel';
import MapDrawControl from './MapDrawControl';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN ?? '';

const BASEMAP_STYLES: Record<string, string> = {
  streets: 'mapbox://styles/mapbox/streets-v12',
  satellite: 'mapbox://styles/mapbox/satellite-streets-v12',
  dark: 'mapbox://styles/mapbox/dark-v11',
  light: 'mapbox://styles/mapbox/light-v11',
  outdoors: 'mapbox://styles/mapbox/outdoors-v12',
};

const BASEMAP_LABELS: Record<string, string> = {
  streets: 'Streets',
  satellite: 'Satellite',
  dark: 'Dark',
  light: 'Light',
  outdoors: 'Outdoors',
};

const BASEMAP_SWATCH: Record<string, string> = {
  streets: '#d4dadc',
  satellite: '#2a3d2a',
  dark: '#242424',
  light: '#f0f2f4',
  outdoors: '#dde8d0',
};

const WEBMAP_BASEMAP_OPTIONS: BasemapOption[] = (Object.keys(BASEMAP_LABELS) as Array<keyof typeof BASEMAP_LABELS>).map(
  (key) => ({
    key,
    label: BASEMAP_LABELS[key],
    styleUrl: BASEMAP_STYLES[key],
    swatch: BASEMAP_SWATCH[key] ?? '#888',
  }),
);

const LAYER_PALETTE = [
  '#1976d2', '#9c27b0', '#00bcd4', '#ff5722',
  '#795548', '#3f51b5', '#cddc39', '#e91e63',
];

const DISPLAY_MODES: { value: LayerDisplayMode; label: string; description: string; icon: React.ReactNode }[] = [
  { value: 'point', label: 'Location', description: 'Single symbol', icon: <PlaceIcon /> },
  { value: 'cluster', label: 'Clusters', description: 'Grouped points', icon: <BubbleChartIcon /> },
  { value: 'heatmap', label: 'Heat Map', description: 'Density surface', icon: <GradientIcon /> },
];

/** Unified option shown in the Add Data autocomplete — either a raw PostGIS layer or a DataHog source. */
type CatalogOption = MapLayerCatalogEntry & {
  sourceId?: number;
  sourceName?: string;
  dataCategory?: string | null;
  sourceStatus?: string | null;
  trackCount?: number;
  groupLabel: string;
};

function buildCatalogOptions(
  postgisLayers: MapLayerCatalogEntry[],
  sourceCatalog: SourceCatalogEntry[] | null,
): CatalogOption[] {
  const options: CatalogOption[] = [];

  if (sourceCatalog && sourceCatalog.length > 0) {
    const sourceTableNames = new Set<string>();
    for (const src of sourceCatalog) {
      sourceTableNames.add(`${src.schema_name}.${src.table_name}`);
      options.push({
        schema: src.schema_name,
        table: src.table_name,
        geomColumn: src.geom_column,
        geomType: 'POINT',
        coordDimension: 2,
        srid: 4326,
        sourceId: src.source_id,
        sourceName: src.source_name,
        dataCategory: src.data_category,
        sourceStatus: src.status,
        trackCount: src.track_count,
        groupLabel: src.data_category || 'Data Sources',
      });
    }

    for (const layer of postgisLayers) {
      if (!sourceTableNames.has(`${layer.schema}.${layer.table}`)) {
        options.push({
          ...layer,
          groupLabel: `${layer.schema} (tables)`,
        });
      }
    }
  } else {
    for (const layer of postgisLayers) {
      options.push({
        ...layer,
        groupLabel: `${layer.schema} (tables)`,
      });
    }
  }

  return options;
}

const LEFT_PANEL_WIDTH = 300;
const RIGHT_PANEL_WIDTH = 320;

interface WebMapEditorDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (opts: WebMapWidgetOptions) => void;
  initialOptions: WebMapWidgetOptions;
}

const CUI_BANNER_HEIGHT = 28;

const WebMapEditorDialog: React.FC<WebMapEditorDialogProps> = ({
  open,
  onClose,
  onSave,
  initialOptions,
}) => {
  const mapRef = useRef<MapRef>(null);
  const theme = useTheme();
  const classificationBannerMode = useFeatureFlagsStore((s) => s.classificationBannerMode);
  const bannerH = classificationBannerMode === 'commercial' ? 0 : CUI_BANNER_HEIGHT;

  // --- Draft state (local until save) ---
  const [layers, setLayers] = useState<WebMapLayerConfig[]>(initialOptions.layers ?? []);
  const [basemap, setBasemap] = useState<string>(initialOptions.basemap ?? 'streets');
  const [controls, setControls] = useState<MapControlToggles>(initialOptions.controls ?? {});
  const [bookmarks, setBookmarks] = useState<MapBookmark[]>(initialOptions.bookmarks ?? []);
  const [annotations, setAnnotations] = useState<MapAnnotation[]>(initialOptions.annotations ?? []);
  const [viewport, setViewport] = useState({
    longitude: initialOptions.initialExtent?.center?.[0] ?? -98.5,
    latitude: initialOptions.initialExtent?.center?.[1] ?? 39.8,
    zoom: initialOptions.initialExtent?.zoom ?? 4,
  });

  // --- Map tools state ---
  const [activeTool, setActiveTool] = useState<MapTool>(null);
  const [showLegend, setShowLegend] = useState(false);
  const [showBookmarks, setShowBookmarks] = useState(false);
  const [measurePoints, setMeasurePoints] = useState<MeasurePoint[]>([]);

  // Reset draft when dialog opens with new initial options
  useEffect(() => {
    if (open) {
      setLayers(initialOptions.layers ?? []);
      setBasemap(initialOptions.basemap ?? 'streets');
      setControls(initialOptions.controls ?? {});
      setBookmarks(initialOptions.bookmarks ?? []);
      setAnnotations(initialOptions.annotations ?? []);
      setViewport({
        longitude: initialOptions.initialExtent?.center?.[0] ?? -98.5,
        latitude: initialOptions.initialExtent?.center?.[1] ?? 39.8,
        zoom: initialOptions.initialExtent?.zoom ?? 4,
      });
      setSelectedLayerId(null);
      setActiveTool(null);
      setShowLegend(false);
      setShowBookmarks(false);
      setMeasurePoints([]);
    }
  }, [open, initialOptions]);

  useEffect(() => {
    if (activeTool !== 'measure') setMeasurePoints([]);
  }, [activeTool]);

  // --- 3D Terrain toggle for editor preview ---
  const editorShowTerrain = controls.terrain3d !== false;
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map) return;
    const apply = () => {
      if (editorShowTerrain) {
        if (!map.getSource('mapbox-dem')) {
          map.addSource('mapbox-dem', {
            type: 'raster-dem',
            url: 'mapbox://mapbox.mapbox-terrain-dem-v1',
            tileSize: 512,
            maxzoom: 14,
          });
        }
        map.setTerrain({ source: 'mapbox-dem', exaggeration: 1.5 });
      } else {
        map.setTerrain(null as any);
      }
    };
    if (map.isStyleLoaded()) {
      apply();
    } else {
      map.once('style.load', apply);
    }
  }, [editorShowTerrain]);

  // --- Left panel tab: 0 = Add Data, 1 = Map Layers, 2 = Controls ---
  const [leftTab, setLeftTab] = useState<number>(0);

  // --- Catalog state ---
  const [rawPostgisLayers, setRawPostgisLayers] = useState<MapLayerCatalogEntry[]>([]);
  const [sourceCatalogEntries, setSourceCatalogEntries] = useState<SourceCatalogEntry[] | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [dbTarget, setDbTarget] = useState<DbTarget>('sandbox');
  const [selectedCatalogEntry, setSelectedCatalogEntry] = useState<CatalogOption | null>(null);
  const catalog = useMemo(
    () => buildCatalogOptions(rawPostgisLayers, dbTarget === 'sandbox' ? sourceCatalogEntries : null),
    [rawPostgisLayers, sourceCatalogEntries, dbTarget],
  );

  // --- Layer selection (drives right panel) ---
  const [selectedLayerId, setSelectedLayerId] = useState<string | null>(null);
  const selectedLayer = useMemo(() => layers.find((l) => l.id === selectedLayerId) ?? null, [layers, selectedLayerId]);

  // --- GeoJSON cache per layer ---
  const [layerGeoJson, setLayerGeoJson] = useState<Map<string, object>>(new Map());
  const [layerSchemas, setLayerSchemas] = useState<Map<string, ColumnInfo[]>>(new Map());
  const [layerColumnsLoading, setLayerColumnsLoading] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0);

  // --- Popup ---
  const [popupInfo, setPopupInfo] = useState<{
    lng: number;
    lat: number;
    properties: Record<string, unknown>;
    layerId?: string;
  } | null>(null);

  // --- Fetch catalog when dialog opens or DB toggle changes ---
  useEffect(() => {
    if (!open) return;
    setCatalogLoading(true);

    const postgisPromise = fetchMapLayerCatalog(dbTarget)
      .then(setRawPostgisLayers)
      .catch(() => setRawPostgisLayers([]));

    const sourcePromise = dbTarget === 'sandbox'
      ? fetchSourceCatalog()
          .then((r) => setSourceCatalogEntries(r.has_external_sources ? r.sources : null))
          .catch(() => setSourceCatalogEntries(null))
      : Promise.resolve(setSourceCatalogEntries(null));

    Promise.all([postgisPromise, sourcePromise]).finally(() => setCatalogLoading(false));
  }, [open, dbTarget]);

  // --- Fetch GeoJSON + columns for each layer ---
  const layersKey = useMemo(
    () => JSON.stringify(layers.map((l) => `${l.db}/${l.schema}/${l.table}/${l.geomColumn}/${l.sourceId ?? ''}`)),
    [layers],
  );

  useEffect(() => {
    if (!open) {
      setLayerColumnsLoading(false);
      return;
    }
    let cancelled = false;
    if (layers.length > 0) setLayerColumnsLoading(true);

    const fetchAll = async () => {
      const featureMap = new Map<string, object>();
      const schemaMap = new Map<string, ColumnInfo[]>();
      try {
        await Promise.all(
          layers.map(async (lyr) => {
            const geojsonPromise = lyr.sourceId != null
              ? fetchSourceGeoJson(lyr.sourceId, 2000)
              : fetchLayerGeoJson(lyr.schema, lyr.table, lyr.geomColumn, 2000, lyr.db);
            const columnsPromise = fetchLayerColumns(lyr.schema, lyr.table, lyr.db);

            const [geojsonResult, columnsResult] = await Promise.allSettled([
              geojsonPromise,
              columnsPromise,
            ]);
            if (cancelled) return;

            const geojson =
              geojsonResult.status === 'fulfilled'
                ? geojsonResult.value
                : { type: 'FeatureCollection', features: [] };
            featureMap.set(lyr.id, geojson);

            const dbCols = columnsResult.status === 'fulfilled' ? columnsResult.value : [];
            const features = (geojson as GeoJSON.FeatureCollection).features ?? [];
            const cols = dbCols.length > 0 ? dbCols : inferColumnsFromFeatures(features);
            schemaMap.set(lyr.id, cols);
          }),
        );
        if (!cancelled) {
          setLayerGeoJson(featureMap);
          setLayerSchemas(schemaMap);
        }
      } finally {
        if (!cancelled) setLayerColumnsLoading(false);
      }
    };

    if (layers.length === 0) {
      setLayerGeoJson(new Map());
      setLayerSchemas(new Map());
      setLayerColumnsLoading(false);
      return;
    }

    fetchAll();
    return () => {
      cancelled = true;
      setLayerColumnsLoading(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layersKey, open, refreshCounter]);

  // --- Auto-refresh timers per layer ---
  useEffect(() => {
    if (!open) return;
    const refreshLayers = layers.filter((l) => l.autoRefresh && l.visible);
    if (refreshLayers.length === 0) return;

    const timers: ReturnType<typeof setInterval>[] = [];
    for (const lyr of refreshLayers) {
      const ms = Math.max((lyr.refreshIntervalMinutes ?? 1) * 60000, 6000);
      const timer = setInterval(async () => {
        try {
          const geojson = lyr.sourceId != null
            ? await fetchSourceGeoJson(lyr.sourceId, 2000)
            : await fetchLayerGeoJson(lyr.schema, lyr.table, lyr.geomColumn, 2000, lyr.db);
          setLayerGeoJson((prev) => {
            const next = new Map(prev);
            next.set(lyr.id, geojson);
            return next;
          });
        } catch { /* silently skip failed refresh */ }
      }, ms);
      timers.push(timer);
    }
    return () => timers.forEach(clearInterval);
  }, [open, layers]);

  // --- Layer CRUD ---

  const handleRemoveLayer = useCallback((id: string) => {
    setLayers((prev) => prev.filter((l) => l.id !== id));
    if (selectedLayerId === id) setSelectedLayerId(null);
  }, [selectedLayerId]);

  const handleToggleVisibility = useCallback((id: string) => {
    setLayers((prev) => prev.map((l) => (l.id === id ? { ...l, visible: !l.visible } : l)));
  }, []);

  const handleMoveLayer = useCallback((id: string, direction: 'up' | 'down') => {
    setLayers((prev) => {
      const idx = prev.findIndex((l) => l.id === id);
      if (idx < 0) return prev;
      const target = direction === 'up' ? idx - 1 : idx + 1;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  }, []);

  const handleUpdateLayer = useCallback((id: string, patch: Partial<WebMapLayerConfig>) => {
    setLayers((prev) => prev.map((l) => (l.id === id ? { ...l, ...patch } : l)));
  }, []);

  // --- Map interaction ---
  const interactiveLayerIds = useMemo(
    () =>
      layers.flatMap((l) => {
        const mode = l.displayMode ?? 'point';
        const prefix = `webmap-${l.id}`;
        if (mode === 'heatmap') return [];
        if (mode === 'cluster') return [`${prefix}-unclustered`, `${prefix}-cluster-circle`];
        const pointLayerId = categoryIconExpression(l) ? `${prefix}-symbol` : `${prefix}-circle`;
        return [`${prefix}-fill`, `${prefix}-line`, pointLayerId];
      }),
    [layers],
  );

  const handleMapClick = useCallback((e: MapLayerMouseEvent) => {
    if (activeTool === 'measure') {
      setMeasurePoints((prev) => [...prev, { lng: e.lngLat.lng, lat: e.lngLat.lat }]);
      return;
    }
    if (activeTool === 'draw') return;

    const features = e.features;
    if (!features?.length) {
      setPopupInfo(null);
      return;
    }
    const f = features[0];
    const props = (f.properties ?? {}) as Record<string, unknown>;
    const map = mapRef.current?.getMap();
    const srcId = typeof f.source === 'string' ? f.source : '';
    const clusterIdRaw = props.cluster_id;
    const pointCount = props.point_count;
    if (map && srcId && clusterIdRaw != null && pointCount != null) {
      const geoSrc = map.getSource(srcId) as GeoJSONSource | undefined;
      if (geoSrc && typeof geoSrc.getClusterExpansionZoom === 'function') {
        const clusterId = typeof clusterIdRaw === 'number' ? clusterIdRaw : Number(clusterIdRaw);
        geoSrc.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err != null || zoom == null) return;
          const geom = f.geometry as GeoJSON.Point;
          const c = geom?.coordinates;
          if (c && c.length >= 2) {
            map.easeTo({ center: [c[0], c[1]], zoom });
          }
        });
        return;
      }
    }
    const matchLayer = layers.find((l) => `webmap-${l.id}` === srcId);
    setPopupInfo({
      lng: e.lngLat.lng,
      lat: e.lngLat.lat,
      properties: props,
      layerId: matchLayer?.id,
    });
  }, [layers, activeTool]);

  // --- Bookmark handlers ---
  const handleAddBookmark = useCallback((bm: MapBookmark) => setBookmarks((prev) => [...prev, bm]), []);
  const handleDeleteBookmark = useCallback((id: string) => setBookmarks((prev) => prev.filter((b) => b.id !== id)), []);
  const handleGoToBookmark = useCallback((bm: MapBookmark) => {
    mapRef.current?.getMap()?.flyTo({ center: [bm.center[0], bm.center[1]], zoom: bm.zoom, bearing: bm.bearing ?? 0, pitch: bm.pitch ?? 0 });
  }, []);

  // --- Annotation handlers ---
  const handleAnnotationCreate = useCallback((ann: MapAnnotation) => setAnnotations((prev) => [...prev, ann]), []);
  const handleAnnotationDelete = useCallback((ids: string[]) => setAnnotations((prev) => prev.filter((a) => !ids.includes(a.id))), []);

  // --- Save ---
  const handleSave = useCallback(() => {
    const opts: WebMapWidgetOptions = {
      layers,
      basemap: basemap as WebMapWidgetOptions['basemap'],
      initialExtent: {
        center: [viewport.longitude, viewport.latitude],
        zoom: viewport.zoom,
      },
      controls,
      bookmarks: bookmarks.length > 0 ? bookmarks : undefined,
      annotations: annotations.length > 0 ? annotations : undefined,
    };
    onSave(opts);
  }, [layers, basemap, viewport, controls, onSave]);

  const mapStyle = BASEMAP_STYLES[basemap] ?? BASEMAP_STYLES.streets;
  const selectedLayerSchema = selectedLayer ? (layerSchemas.get(selectedLayer.id) ?? []) : [];
  const selectedLayerFeatures = useMemo(() => {
    if (!selectedLayer) return [];
    const collection = layerGeoJson.get(selectedLayer.id) as GeoJSON.FeatureCollection | undefined;
    return collection?.features ?? [];
  }, [layerGeoJson, selectedLayer]);
  const selectedCategoryValueCount = useMemo(() => {
    const field = selectedLayer?.categoricalStyle?.field;
    if (!field) return 0;
    const values = new Set<string>();
    selectedLayerFeatures.forEach((feature) => {
      const raw = feature.properties?.[field];
      if (raw != null && typeof raw !== 'object') values.add(String(raw));
    });
    return values.size;
  }, [selectedLayer, selectedLayerFeatures]);

  const handleCategoryFieldChange = useCallback((field: string) => {
    if (!selectedLayer) return;
    if (!field) {
      handleUpdateLayer(selectedLayer.id, { categoricalStyle: undefined });
      return;
    }
    const existing = selectedLayer.categoricalStyle?.field === field
      ? selectedLayer.categoricalStyle
      : undefined;
    handleUpdateLayer(selectedLayer.id, {
      categoricalStyle: {
        field,
        categories: deriveCategoryStyles(selectedLayerFeatures, field, existing),
        fallbackColor: existing?.fallbackColor ?? selectedLayer.fillColor,
        fallbackIcon: existing?.fallbackIcon,
      },
    });
  }, [handleUpdateLayer, selectedLayer, selectedLayerFeatures]);

  const handleRefreshCategories = useCallback(() => {
    if (!selectedLayer?.categoricalStyle) return;
    const style = selectedLayer.categoricalStyle;
    handleUpdateLayer(selectedLayer.id, {
      categoricalStyle: {
        ...style,
        categories: deriveCategoryStyles(selectedLayerFeatures, style.field, style),
      },
    });
  }, [handleUpdateLayer, selectedLayer, selectedLayerFeatures]);

  const editorPopupEntries = useMemo(() => {
    if (!popupInfo) return [];
    const layer = popupInfo.layerId ? layers.find((l) => l.id === popupInfo.layerId) : undefined;
    const order = layer?.id ? (layerSchemas.get(layer.id)?.map((c) => c.name) ?? []) : [];
    return popupPropertyEntries(popupInfo.properties, layer?.popupFields, order.length ? order : undefined);
  }, [popupInfo, layers, layerSchemas]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: 'background.default',
          pt: `${bannerH}px`,
          pb: `${bannerH}px`,
        },
      }}
      sx={{ zIndex: theme.zIndex.modal + 1 }}
    >
      {/* ── Top bar ── */}
      <AppBar position="static" color="default" elevation={1} sx={{ zIndex: 2 }}>
        <Toolbar variant="dense" sx={{ gap: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Map Editor
          </Typography>

          <Divider orientation="vertical" flexItem />

          <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
              Basemap
            </Typography>
            <Box sx={{ overflowX: 'auto', py: 0.5 }}>
              <MapboxBasemapStrip
                accessToken={MAPBOX_TOKEN}
                value={basemap}
                options={WEBMAP_BASEMAP_OPTIONS}
                onChange={setBasemap}
              />
            </Box>
          </Stack>

          <Tooltip title="Refresh all layer data">
            <IconButton
              size="small"
              onClick={() => setRefreshCounter((c) => c + 1)}
              disabled={layers.length === 0 || layerColumnsLoading}
            >
              <RefreshIcon fontSize="small" sx={{ ...(layerColumnsLoading && { animation: 'spin 1s linear infinite', '@keyframes spin': { '100%': { transform: 'rotate(360deg)' } } }) }} />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            size="small"
            startIcon={<SaveIcon />}
            onClick={handleSave}
          >
            Save map
          </Button>
          <IconButton edge="end" onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* ── Body: left panel | map | right panel ── */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        {/* ── Left panel: layers ── */}
        <Box
          sx={{
            width: LEFT_PANEL_WIDTH,
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            borderRight: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
            overflow: 'hidden',
          }}
        >
          {/* ── Tab bar ── */}
          <Tabs
            value={leftTab}
            onChange={(_, v) => setLeftTab(v)}
            variant="fullWidth"
            sx={{ minHeight: 40, borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab
              icon={<StorageIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Add Data"
              sx={{ minHeight: 40, textTransform: 'none', fontSize: '0.8rem' }}
            />
            <Tab
              icon={<LayersIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label={`Layers${layers.length > 0 ? ` (${layers.length})` : ''}`}
              sx={{ minHeight: 40, textTransform: 'none', fontSize: '0.8rem' }}
            />
            <Tab
              icon={<TuneIcon sx={{ fontSize: 18 }} />}
              iconPosition="start"
              label="Controls"
              sx={{ minHeight: 40, textTransform: 'none', fontSize: '0.8rem' }}
            />
          </Tabs>

          {/* ── Tab 0: Add Data ── */}
          {leftTab === 0 && (
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              <Box sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
                <ToggleButtonGroup
                  value={dbTarget}
                  exclusive
                  onChange={(_, v) => v && setDbTarget(v)}
                  size="small"
                  fullWidth
                  sx={{ mb: 1 }}
                >
                  <ToggleButton value="app">App DB</ToggleButton>
                  <ToggleButton value="sandbox">Sandbox DB</ToggleButton>
                </ToggleButtonGroup>

                <Autocomplete<CatalogOption>
                  size="small"
                  fullWidth
                  openOnFocus
                  options={catalog}
                  loading={catalogLoading}
                  value={selectedCatalogEntry}
                  groupBy={(o) => o.groupLabel}
                  onChange={(_, v) => {
                    setSelectedCatalogEntry(v);
                    if (v) {
                      const idx = layers.length;
                      const newLayer: WebMapLayerConfig = {
                        id: `wml_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
                        schema: v.schema,
                        table: v.table,
                        geomColumn: v.geomColumn,
                        db: dbTarget,
                        visible: true,
                        opacity: 1,
                        fillColor: LAYER_PALETTE[idx % LAYER_PALETTE.length],
                        strokeColor: LAYER_PALETTE[idx % LAYER_PALETTE.length],
                        strokeWidth: 2,
                        pointRadius: 5,
                        ...(v.sourceId != null && { sourceId: v.sourceId, sourceName: v.sourceName }),
                      };
                      setLayers((prev) => [...prev, newLayer]);
                      setSelectedLayerId(newLayer.id);
                      setSelectedCatalogEntry(null);
                      setLeftTab(1);
                    }
                  }}
                  getOptionLabel={(o) => o.sourceName ?? `${o.schema}.${o.table}`}
                  noOptionsText={
                    catalogLoading
                      ? 'Loading...'
                      : `No PostGIS layers in ${dbTarget === 'sandbox' ? 'Sandbox' : 'App'} DB`
                  }
                  renderOption={(props, o) => {
                    const alreadyAdded = o.sourceId != null
                      ? layers.some((l) => l.sourceId === o.sourceId)
                      : layers.some(
                          (l) => l.schema === o.schema && l.table === o.table && l.geomColumn === o.geomColumn && l.db === dbTarget && l.sourceId == null,
                        );
                    const label = o.sourceName ?? `${o.schema}.${o.table}`;
                    const subtitle = o.sourceId != null
                      ? `${o.trackCount?.toLocaleString() ?? 0} tracks · ${o.sourceStatus ?? ''}`
                      : `${o.geomType} (${o.geomColumn})`;
                    return (
                      <li {...props} key={o.sourceId != null ? `src-${o.sourceId}` : `${o.schema}.${o.table}.${o.geomColumn}`}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                          <AddIcon fontSize="small" color={alreadyAdded ? 'disabled' : 'primary'} />
                          <Stack sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" noWrap>
                              {label}
                              {alreadyAdded && (
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                                  (added)
                                </Typography>
                              )}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {subtitle}
                            </Typography>
                          </Stack>
                        </Stack>
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Click to browse layers..."
                      helperText={
                        !catalogLoading && catalog.length > 0
                          ? `${catalog.length} layer${catalog.length !== 1 ? 's' : ''} available — click to add`
                          : !catalogLoading && catalog.length === 0
                            ? dbTarget === 'sandbox'
                              ? 'No layers found — check DB connection'
                              : 'Try switching to Sandbox DB'
                            : undefined
                      }
                    />
                  )}
                  isOptionEqualToValue={(o, v) =>
                    o.sourceId != null
                      ? o.sourceId === v.sourceId
                      : o.schema === v.schema && o.table === v.table && o.geomColumn === v.geomColumn
                  }
                  slotProps={{
                    popper: { sx: { zIndex: theme.zIndex.modal + 10 } },
                  }}
                />
              </Box>

              {layers.length > 0 && (
                <Box sx={{ px: 1.5, pb: 1, mt: 'auto' }}>
                  <Divider sx={{ mb: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    {layers.length} layer{layers.length !== 1 ? 's' : ''} on map.{' '}
                    <Typography
                      component="span"
                      variant="caption"
                      color="primary"
                      sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                      onClick={() => setLeftTab(1)}
                    >
                      View layers
                    </Typography>
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* ── Tab 1: Map Layers ── */}
          {leftTab === 1 && (
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              {layers.length === 0 ? (
                <Box sx={{ px: 2, py: 4, textAlign: 'center' }}>
                  <LayersIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    No layers added yet
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={() => setLeftTab(0)}
                  >
                    Add Data
                  </Button>
                </Box>
              ) : (
                <List dense disablePadding>
                  {layers.map((lyr, idx) => (
                    <ListItemButton
                      key={lyr.id}
                      selected={selectedLayerId === lyr.id}
                      onClick={() => setSelectedLayerId(selectedLayerId === lyr.id ? null : lyr.id)}
                      sx={{ py: 0.5 }}
                    >
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <Box
                          sx={{
                            width: 14,
                            height: 14,
                            borderRadius: '3px',
                            bgcolor: lyr.fillColor,
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={lyr.sourceName ?? lyr.table}
                        secondary={lyr.sourceName ? lyr.table : lyr.schema}
                        primaryTypographyProps={{ variant: 'body2', noWrap: true, fontWeight: selectedLayerId === lyr.id ? 600 : 400 }}
                        secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                      />
                      <Stack direction="row" spacing={0} sx={{ ml: 'auto', flexShrink: 0 }}>
                        <Tooltip title={lyr.visible ? 'Hide' : 'Show'}>
                          <IconButton
                            size="small"
                            onClick={(e) => { e.stopPropagation(); handleToggleVisibility(lyr.id); }}
                          >
                            {lyr.visible ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Move up">
                          <span>
                            <IconButton
                              size="small"
                              disabled={idx === 0}
                              onClick={(e) => { e.stopPropagation(); handleMoveLayer(lyr.id, 'up'); }}
                            >
                              <ArrowUpwardIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Move down">
                          <span>
                            <IconButton
                              size="small"
                              disabled={idx === layers.length - 1}
                              onClick={(e) => { e.stopPropagation(); handleMoveLayer(lyr.id, 'down'); }}
                            >
                              <ArrowDownwardIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </ListItemButton>
                  ))}
                </List>
              )}
            </Box>
          )}

          {/* ── Tab 2: Controls ── */}
          {leftTab === 2 && (
            <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Map Controls
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
                Toggle which controls appear on the map.
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={controls.navigation !== false}
                      onChange={(_, v) => setControls((c) => ({ ...c, navigation: v }))}
                      size="small"
                    />
                  }
                  label="Zoom & Compass"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={controls.geolocate !== false}
                      onChange={(_, v) => setControls((c) => ({ ...c, geolocate: v }))}
                      size="small"
                    />
                  }
                  label="Geolocate (my position)"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={controls.fullscreen !== false}
                      onChange={(_, v) => setControls((c) => ({ ...c, fullscreen: v }))}
                      size="small"
                    />
                  }
                  label="Fullscreen"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={controls.scale !== false}
                      onChange={(_, v) => setControls((c) => ({ ...c, scale: v }))}
                      size="small"
                    />
                  }
                  label="Scale Bar"
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={controls.terrain3d !== false}
                      onChange={(_, v) => setControls((c) => ({ ...c, terrain3d: v }))}
                      size="small"
                    />
                  }
                  label="3D Terrain"
                />
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                  Dashboard Integration
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={controls.filterByExtent !== false}
                      onChange={(_, v) => setControls((c) => ({ ...c, filterByExtent: v }))}
                      size="small"
                    />
                  }
                  label="Filter by Extent"
                />
              </FormGroup>
            </Box>
          )}
        </Box>

        {/* ── Center: Map ── */}
        <Box sx={{ flex: 1, position: 'relative' }}>
          <ReactMapGL
            ref={mapRef}
            {...viewport}
            onMove={(e) => setViewport(e.viewState)}
            onClick={handleMapClick}
            interactiveLayerIds={activeTool === 'measure' || activeTool === 'draw' ? [] : interactiveLayerIds}
            mapStyle={mapStyle}
            mapboxAccessToken={MAPBOX_TOKEN}
            projection={{name: 'mercator'}}
            style={{ width: '100%', height: '100%' }}
            cursor={activeTool === 'measure' || activeTool === 'draw' ? 'crosshair' : undefined}
            reuseMaps
          >
            {controls.navigation !== false && <NavigationControl position="top-right" />}
            {controls.geolocate !== false && <GeolocateControl position="top-right" />}
            {controls.fullscreen !== false && <FullscreenControl position="top-right" />}
            {controls.scale !== false && <ScaleControl position="bottom-left" />}

            {layers.map((lyr, idx) => {
              if (!lyr.visible) return null;
              const srcId = `webmap-${lyr.id}`;
              const sourceKey = webMapGeoJsonSourceKey(srcId, lyr);
              const geojsonData = layerGeoJson.get(lyr.id) ?? { type: 'FeatureCollection', features: [] };
              const fill = lyr.fillColor || LAYER_PALETTE[idx % LAYER_PALETTE.length];
              const stroke = lyr.strokeColor || fill;
              const mode = lyr.displayMode ?? 'point';
              const categorizedColor = categoryColorExpression(lyr, fill);
              const pointIcon = categoryIconExpression(lyr);

              /* ── Heatmap ── */
              if (mode === 'heatmap') {
                return (
                  <Source key={sourceKey} id={srcId} type="geojson" data={geojsonData as GeoJSON.FeatureCollection}>
                    <Layer
                      id={`${srcId}-heat`}
                      type="heatmap"
                      filter={['match', ['geometry-type'], ['Point', 'MultiPoint'], true, false]}
                      paint={{
                        'heatmap-radius': lyr.heatmapRadius ?? 20,
                        'heatmap-intensity': lyr.heatmapIntensity ?? 1,
                        'heatmap-opacity': lyr.opacity,
                        ...heatmapWeightPaint(lyr.heatmapWeight),
                        'heatmap-color': [
                          'interpolate', ['linear'], ['heatmap-density'],
                          0, 'rgba(0,0,255,0)',
                          0.2, 'royalblue',
                          0.4, 'cyan',
                          0.6, 'lime',
                          0.8, 'yellow',
                          1, 'red',
                        ],
                      } as HeatmapPaint}
                    />
                  </Source>
                );
              }

              /* ── Cluster ── */
              if (mode === 'cluster') {
                return (
                  <Source
                    key={sourceKey}
                    id={srcId}
                    type="geojson"
                    data={geojsonData as GeoJSON.FeatureCollection}
                    cluster
                    clusterRadius={lyr.clusterRadius ?? 50}
                    clusterMaxZoom={lyr.clusterMaxZoom ?? 14}
                  >
                    <Layer
                      id={`${srcId}-cluster-circle`}
                      type="circle"
                      filter={['has', 'point_count']}
                      paint={{
                        'circle-color': [
                          'step', ['get', 'point_count'],
                          fill,
                          50, fill,
                          200, fill,
                        ],
                        'circle-radius': ['step', ['get', 'point_count'], 18, 50, 24, 200, 32],
                        'circle-opacity': lyr.opacity,
                        'circle-stroke-width': lyr.strokeWidth ?? 2,
                        'circle-stroke-color': '#fff',
                      }}
                    />
                    <Layer
                      id={`${srcId}-cluster-count`}
                      type="symbol"
                      filter={['has', 'point_count']}
                      layout={{
                        'text-field': ['get', 'point_count_abbreviated'] as unknown as string,
                        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
                        'text-size': 12,
                        'text-allow-overlap': true,
                      }}
                      paint={{ 'text-color': '#fff' }}
                    />
                    <Layer
                      id={`${srcId}-unclustered`}
                      type="circle"
                      filter={['!', ['has', 'point_count']]}
                      paint={{
                        'circle-radius': lyr.pointRadius ?? 5,
                        'circle-color': stroke,
                        'circle-stroke-width': 1,
                        'circle-stroke-color': '#fff',
                        'circle-opacity': lyr.opacity,
                      }}
                    />
                  </Source>
                );
              }

              /* ── Point / Location (default) ── */
              return (
                <Source key={sourceKey} id={srcId} type="geojson" data={geojsonData as GeoJSON.FeatureCollection}>
                  <Layer
                    id={`${srcId}-fill`}
                    type="fill"
                    filter={['match', ['geometry-type'], ['Polygon', 'MultiPolygon'], true, false]}
                    paint={{ 'fill-color': categorizedColor, 'fill-opacity': lyr.opacity * 0.3 } as any}
                  />
                  <Layer
                    id={`${srcId}-line`}
                    type="line"
                    filter={['match', ['geometry-type'], ['Polygon', 'MultiPolygon', 'LineString', 'MultiLineString'], true, false]}
                    paint={{ 'line-color': lyr.categoricalStyle ? categorizedColor : stroke, 'line-width': lyr.strokeWidth ?? 2, 'line-opacity': lyr.opacity } as any}
                  />
                  {pointIcon ? (
                    <Layer
                      key={`${srcId}-symbol`}
                      id={`${srcId}-symbol`}
                      type="symbol"
                      filter={['match', ['geometry-type'], ['Point', 'MultiPoint'], true, false]}
                      layout={{
                        'icon-image': pointIcon as unknown as string,
                        'icon-size': lyr.iconSize ?? 1,
                        'icon-allow-overlap': true,
                        'icon-ignore-placement': true,
                      }}
                      paint={{ 'icon-opacity': lyr.opacity, 'icon-color': categorizedColor } as any}
                    />
                  ) : (
                    <Layer
                      key={`${srcId}-circle`}
                      id={`${srcId}-circle`}
                      type="circle"
                      filter={['match', ['geometry-type'], ['Point', 'MultiPoint'], true, false]}
                      paint={{
                        'circle-radius': lyr.pointRadius ?? 5,
                        'circle-color': categorizedColor,
                        'circle-stroke-width': lyr.strokeWidth ?? 1,
                        'circle-stroke-color': stroke,
                        'circle-opacity': lyr.opacity,
                      } as any}
                    />
                  )}
                </Source>
              );
            })}

            {/* Measure overlay */}
            {activeTool === 'measure' && (
              <MapMeasureOverlay
                points={measurePoints}
                onUndo={() => setMeasurePoints((p) => p.slice(0, -1))}
                onClear={() => setMeasurePoints([])}
              />
            )}

            {/* Draw control */}
            {activeTool === 'draw' && (
              <MapDrawControl
                onAnnotationCreate={handleAnnotationCreate}
                onAnnotationDelete={handleAnnotationDelete}
              />
            )}

            {popupInfo && (
              <Popup
                longitude={popupInfo.lng}
                latitude={popupInfo.lat}
                onClose={() => setPopupInfo(null)}
                closeOnClick={false}
                maxWidth="320px"
              >
                <Stack spacing={0.5} sx={{ maxHeight: 200, overflow: 'auto', p: 0.5 }}>
                  {editorPopupEntries.map(([k, v]) => (
                    <Typography key={k} variant="caption" component="div">
                      <strong>{k}:</strong> {String(v ?? '')}
                    </Typography>
                  ))}
                </Stack>
              </Popup>
            )}
          </ReactMapGL>

          {/* Map Toolbar */}
          <MapToolbar
            activeTool={activeTool}
            onToolChange={setActiveTool}
            showLegend={showLegend}
            onToggleLegend={() => setShowLegend((v) => !v)}
            showBookmarks={showBookmarks}
            onToggleBookmarks={() => setShowBookmarks((v) => !v)}
          />

          {/* Legend overlay */}
          {showLegend && (
            <MapLegendOverlay
              layers={layers}
              onClose={() => setShowLegend(false)}
              onToggleVisibility={(id) =>
                setLayers((prev) => prev.map((l) => (l.id === id ? { ...l, visible: !l.visible } : l)))
              }
            />
          )}

          {/* Bookmarks panel */}
          {showBookmarks && (
            <MapBookmarksPanel
              bookmarks={bookmarks}
              onGoTo={handleGoToBookmark}
              onAdd={handleAddBookmark}
              onDelete={handleDeleteBookmark}
              onClose={() => setShowBookmarks(false)}
              currentCenter={[viewport.longitude, viewport.latitude]}
              currentZoom={viewport.zoom}
            />
          )}
        </Box>

        {/* ── Right panel: selected layer properties ── */}
        {selectedLayer && (
          <Box
            sx={{
              width: RIGHT_PANEL_WIDTH,
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              borderLeft: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              overflow: 'hidden',
            }}
          >
            {/* Header */}
            <Stack direction="row" alignItems="center" sx={{ px: 1.5, py: 1 }}>
              <Tooltip title="Close properties">
                <IconButton size="small" onClick={() => setSelectedLayerId(null)} sx={{ mr: 1 }}>
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" noWrap>{selectedLayer.sourceName ?? selectedLayer.table}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {selectedLayer.sourceName ? selectedLayer.table : selectedLayer.schema} &middot; {selectedLayer.db === 'sandbox' ? 'Sandbox' : 'App'} DB
                </Typography>
              </Box>
            </Stack>
            <Divider />

            <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 1.5 }}>
              <Stack spacing={2.5}>
                {/* ── Display Style Picker ── */}
                <Box>
                  <Typography variant="overline" gutterBottom>Display Style</Typography>
                  <Stack direction="row" spacing={1}>
                    {DISPLAY_MODES.map((mode) => {
                      const active = (selectedLayer.displayMode ?? 'point') === mode.value;
                      return (
                        <Card
                          key={mode.value}
                          variant="outlined"
                          sx={{
                            flex: 1,
                            border: 2,
                            borderColor: active ? 'primary.main' : 'divider',
                            bgcolor: active ? 'action.selected' : 'transparent',
                          }}
                        >
                          <CardActionArea
                            onClick={() => handleUpdateLayer(selectedLayer.id, { displayMode: mode.value })}
                            sx={{ p: 1, textAlign: 'center' }}
                          >
                            <Box sx={{ color: active ? 'primary.main' : 'text.secondary', mb: 0.5 }}>
                              {mode.icon}
                            </Box>
                            <Typography variant="caption" sx={{ fontWeight: active ? 600 : 400, display: 'block', lineHeight: 1.2 }}>
                              {mode.label}
                            </Typography>
                          </CardActionArea>
                        </Card>
                      );
                    })}
                  </Stack>
                </Box>

                <Divider />

                {/* ── Point / Location mode options ── */}
                {(selectedLayer.displayMode ?? 'point') === 'point' && (
                  <Box>
                    <Typography variant="overline" gutterBottom>Symbology</Typography>
                    <Stack spacing={1.5}>
                      <SettingsColorPicker
                        label="Fill color"
                        value={selectedLayer.fillColor}
                        onChange={(v) => handleUpdateLayer(selectedLayer.id, { fillColor: v })}
                      />
                      <SettingsColorPicker
                        label="Stroke color"
                        value={selectedLayer.strokeColor}
                        onChange={(v) => handleUpdateLayer(selectedLayer.id, { strokeColor: v })}
                      />
                      <SettingsSlider
                        label="Point radius"
                        value={selectedLayer.pointRadius}
                        onChange={(v) => handleUpdateLayer(selectedLayer.id, { pointRadius: v })}
                        min={1} max={20} unit="px"
                      />
                      <MapboxSpriteIconPicker
                        accessToken={MAPBOX_TOKEN}
                        mapStyleUrl={mapStyle}
                        selectedIconId={selectedLayer.mapboxIcon}
                        onSelectIcon={(iconId) => handleUpdateLayer(selectedLayer.id, { mapboxIcon: iconId })}
                        onClearIcon={() => handleUpdateLayer(selectedLayer.id, { mapboxIcon: undefined })}
                      />
                      {resolveSavedSpriteIcon(selectedLayer.mapboxIcon) && selectedLayer.mapboxIcon && (
                        <SettingsSlider
                          label="Icon size"
                          value={selectedLayer.iconSize ?? 1}
                          onChange={(v) => handleUpdateLayer(selectedLayer.id, { iconSize: v })}
                          min={0.3} max={3} step={0.1} unit="x"
                        />
                      )}
                      {selectedLayerSchema.length > 0 && (
                        <>
                          <Divider />
                          <FormControl size="small" fullWidth>
                            <InputLabel id="webmap-category-field">Style by field</InputLabel>
                            <Select
                              labelId="webmap-category-field"
                              label="Style by field"
                              value={selectedLayer.categoricalStyle?.field ?? ''}
                              onChange={(e) => handleCategoryFieldChange(String(e.target.value))}
                            >
                              <MenuItem value=""><em>Single style</em></MenuItem>
                              {selectedLayerSchema
                                .filter((column) => column.name !== selectedLayer.geomColumn)
                                .map((column) => (
                                  <MenuItem key={column.name} value={column.name}>
                                    {column.name}
                                  </MenuItem>
                                ))}
                            </Select>
                          </FormControl>
                          {selectedLayer.categoricalStyle && (
                            <Stack spacing={1}>
                              <Stack direction="row" alignItems="center" justifyContent="space-between">
                                <Typography variant="caption" color="text.secondary">
                                  {selectedLayer.categoricalStyle.categories.length} categor{selectedLayer.categoricalStyle.categories.length === 1 ? 'y' : 'ies'}
                                </Typography>
                                <Button size="small" onClick={handleRefreshCategories} startIcon={<RefreshIcon />}>
                                  Refresh values
                                </Button>
                              </Stack>
                              {selectedCategoryValueCount > MAX_WEB_MAP_CATEGORIES && (
                                <Typography variant="caption" color="warning.main">
                                  Showing the first {MAX_WEB_MAP_CATEGORIES} of {selectedCategoryValueCount} values. Remaining values use the fallback style.
                                </Typography>
                              )}
                              {selectedLayer.categoricalStyle.categories.length === 0 && (
                                <Typography variant="caption" color="text.secondary">
                                  No non-empty values were found in the loaded features.
                                </Typography>
                              )}
                              {selectedLayer.categoricalStyle.categories.map((category, categoryIndex) => (
                                <Box
                                  key={category.value}
                                  sx={{
                                    p: 1,
                                    border: 1,
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                  }}
                                >
                                  <Typography variant="caption" fontWeight={600} noWrap title={category.label}>
                                    {category.label}
                                  </Typography>
                                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
                                    <Box
                                      component="input"
                                      type="color"
                                      aria-label={`Color for ${category.label}`}
                                      value={category.color}
                                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                        const categories = [...selectedLayer.categoricalStyle!.categories];
                                        categories[categoryIndex] = { ...category, color: e.target.value };
                                        handleUpdateLayer(selectedLayer.id, {
                                          categoricalStyle: { ...selectedLayer.categoricalStyle!, categories },
                                        });
                                      }}
                                      sx={{ width: 34, height: 30, p: 0, border: 0, bgcolor: 'transparent', cursor: 'pointer' }}
                                    />
                                    <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                                      <InputLabel>Symbol</InputLabel>
                                      <Select
                                        label="Symbol"
                                        value={category.icon ?? ''}
                                        onChange={(e) => {
                                          const categories = [...selectedLayer.categoricalStyle!.categories];
                                          categories[categoryIndex] = {
                                            ...category,
                                            icon: e.target.value ? String(e.target.value) : undefined,
                                          };
                                          handleUpdateLayer(selectedLayer.id, {
                                            categoricalStyle: { ...selectedLayer.categoricalStyle!, categories },
                                          });
                                        }}
                                      >
                                        <MenuItem value=""><em>Default</em></MenuItem>
                                        {MAPBOX_STYLE_SPRITE_ICONS.map((icon) => (
                                          <MenuItem key={icon} value={icon}>{icon}</MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </Stack>
                                </Box>
                              ))}
                              <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                                <Typography variant="caption" fontWeight={600}>
                                  Other / empty values
                                </Typography>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.75 }}>
                                  <Box
                                    component="input"
                                    type="color"
                                    aria-label="Fallback category color"
                                    value={selectedLayer.categoricalStyle.fallbackColor}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                      handleUpdateLayer(selectedLayer.id, {
                                        categoricalStyle: {
                                          ...selectedLayer.categoricalStyle!,
                                          fallbackColor: e.target.value,
                                        },
                                      })
                                    }
                                    sx={{ width: 34, height: 30, p: 0, border: 0, bgcolor: 'transparent', cursor: 'pointer' }}
                                  />
                                  <Typography variant="caption" color="text.secondary">
                                    Fallback color
                                  </Typography>
                                </Stack>
                              </Box>
                            </Stack>
                          )}
                        </>
                      )}
                    </Stack>
                  </Box>
                )}

                {/* ── Cluster mode options ── */}
                {selectedLayer.displayMode === 'cluster' && (
                  <Box>
                    <Typography variant="overline" gutterBottom>Cluster Options</Typography>
                    <Stack spacing={1.5}>
                      <SettingsColorPicker
                        label="Cluster color"
                        value={selectedLayer.fillColor}
                        onChange={(v) => handleUpdateLayer(selectedLayer.id, { fillColor: v })}
                      />
                      <SettingsSlider
                        label="Cluster radius"
                        value={selectedLayer.clusterRadius ?? 50}
                        onChange={(v) => handleUpdateLayer(selectedLayer.id, { clusterRadius: v })}
                        min={10} max={150} step={5} unit="px"
                      />
                      <SettingsSlider
                        label="Max cluster zoom"
                        value={selectedLayer.clusterMaxZoom ?? 14}
                        onChange={(v) => handleUpdateLayer(selectedLayer.id, { clusterMaxZoom: v })}
                        min={5} max={20}
                      />
                      <SettingsColorPicker
                        label="Unclustered point color"
                        value={selectedLayer.strokeColor}
                        onChange={(v) => handleUpdateLayer(selectedLayer.id, { strokeColor: v })}
                      />
                      <SettingsSlider
                        label="Point radius"
                        value={selectedLayer.pointRadius}
                        onChange={(v) => handleUpdateLayer(selectedLayer.id, { pointRadius: v })}
                        min={1} max={20} unit="px"
                      />
                    </Stack>
                  </Box>
                )}

                {/* ── Heatmap mode options ── */}
                {selectedLayer.displayMode === 'heatmap' && (
                  <Box>
                    <Typography variant="overline" gutterBottom>Heatmap Options</Typography>
                    <Stack spacing={1.5}>
                      <SettingsSlider
                        label="Radius"
                        value={selectedLayer.heatmapRadius ?? 20}
                        onChange={(v) => handleUpdateLayer(selectedLayer.id, { heatmapRadius: v })}
                        min={5} max={80} unit="px"
                      />
                      <SettingsSlider
                        label="Intensity"
                        value={selectedLayer.heatmapIntensity ?? 1}
                        onChange={(v) => handleUpdateLayer(selectedLayer.id, { heatmapIntensity: v })}
                        min={0.1} max={5} step={0.1}
                      />
                      {selectedLayerSchema.length > 0 && (
                        <Autocomplete
                          size="small"
                          options={[
                            '(uniform)',
                            ...selectedLayerSchema
                              .filter((c) =>
                                ['int2', 'int4', 'int8', 'float4', 'float8', 'numeric', 'serial', 'serial8'].includes(
                                  (c.udtName || '').toLowerCase(),
                                ),
                              )
                              .map((c) => c.name),
                          ]}
                          value={selectedLayer.heatmapWeight || '(uniform)'}
                          onChange={(_, v) => handleUpdateLayer(selectedLayer.id, { heatmapWeight: v === '(uniform)' ? undefined : (v ?? undefined) })}
                          renderInput={(params) => <TextField {...params} label="Weight field" />}
                        />
                      )}
                    </Stack>
                  </Box>
                )}

                <Divider />

                {/* ── Common Appearance ── */}
                <Box>
                  <Typography variant="overline" gutterBottom>Appearance</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    Opacity and colors here are saved with the map when you click Save map.
                  </Typography>
                  <Stack spacing={1.5}>
                    <SettingsOpacitySlider
                      label="Opacity"
                      value={selectedLayer.opacity}
                      onChange={(v) => handleUpdateLayer(selectedLayer.id, { opacity: v })}
                      min={0}
                      max={1}
                    />
                    {(selectedLayer.displayMode ?? 'point') !== 'heatmap' && (
                      <SettingsSlider
                        label="Stroke width"
                        value={selectedLayer.strokeWidth}
                        onChange={(v) => handleUpdateLayer(selectedLayer.id, { strokeWidth: v })}
                        min={0.5} max={8} step={0.5} unit="px"
                      />
                    )}
                  </Stack>
                </Box>

                <Divider />

                {/* ── Refresh Interval ── */}
                <Box>
                  <Typography variant="overline" gutterBottom>Refresh Interval</Typography>
                  <Stack spacing={1.5}>
                    <FormControlLabel
                      control={
                        <Switch
                          size="small"
                          checked={selectedLayer.autoRefresh === true}
                          onChange={(_, v) => handleUpdateLayer(selectedLayer.id, { autoRefresh: v })}
                        />
                      }
                      label="Automatically refresh layer"
                    />
                    {selectedLayer.autoRefresh && (
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                          Refresh every
                        </Typography>
                        <TextField
                          size="small"
                          type="number"
                          value={selectedLayer.refreshIntervalMinutes ?? 1}
                          onChange={(e) => {
                            const v = parseFloat(e.target.value);
                            if (!isNaN(v) && v >= 0.1) {
                              handleUpdateLayer(selectedLayer.id, { refreshIntervalMinutes: v });
                            }
                          }}
                          inputProps={{ min: 0.1, step: 0.1 }}
                          sx={{ width: 90 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          minute(s)
                        </Typography>
                      </Stack>
                    )}
                  </Stack>
                </Box>

                <Divider />

                {/* ── Fields (from /map-layers/columns) ── */}
                <Box>
                  <Typography variant="overline" gutterBottom>Fields</Typography>
                  {layerColumnsLoading ? (
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 2 }}>
                      <CircularProgress size={20} />
                      <Typography variant="caption" color="text.secondary">
                        Loading column list from database…
                      </Typography>
                    </Stack>
                  ) : selectedLayerSchema.length === 0 ? (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', py: 1 }}>
                      No columns loaded for this layer. Check the connection or re-add the layer.
                    </Typography>
                  ) : (
                    <Stack spacing={2}>
                      {(selectedLayer.displayMode ?? 'point') !== 'heatmap' && (
                        <FormControl size="small" fullWidth>
                          <InputLabel id="webmap-label-field">Map label field</InputLabel>
                          <Select
                            labelId="webmap-label-field"
                            label="Map label field"
                            value={selectedLayer.labelField ?? ''}
                            onChange={(e) => {
                              const v = e.target.value;
                              handleUpdateLayer(selectedLayer.id, {
                                labelField: v === '' ? undefined : String(v),
                              });
                            }}
                            renderValue={(v) => {
                              if (!v) return <em>None</em>;
                              const c = selectedLayerSchema.find((x) => x.name === v);
                              return c ? `${c.name} (${c.type})` : String(v);
                            }}
                          >
                            <MenuItem value="">
                              <em>None</em>
                            </MenuItem>
                            {selectedLayerSchema.map((c) => (
                              <MenuItem key={c.name} value={c.name}>
                                <ListItemText
                                  primary={c.name}
                                  secondary={`${c.type}${c.udtName && c.udtName !== c.type ? ` · ${c.udtName}` : ''}`}
                                  primaryTypographyProps={{ variant: 'body2' }}
                                  secondaryTypographyProps={{ variant: 'caption' }}
                                />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}

                      <Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                          Popup attributes — loaded from table schema (no typing required).
                        </Typography>
                        <FormControlLabel
                          control={(
                            <Switch
                              size="small"
                              checked={selectedLayer.popupFields !== undefined}
                              onChange={(_, on) => {
                                if (on) {
                                  handleUpdateLayer(selectedLayer.id, {
                                    popupFields: selectedLayerSchema.map((c) => c.name),
                                  });
                                } else {
                                  handleUpdateLayer(selectedLayer.id, { popupFields: undefined });
                                }
                              }}
                            />
                          )}
                          label="Limit popup to selected columns"
                        />
                        {selectedLayer.popupFields !== undefined && (
                          <Box sx={{ mt: 1 }}>
                            <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleUpdateLayer(selectedLayer.id, {
                                  popupFields: selectedLayerSchema.map((c) => c.name),
                                })}
                              >
                                Select all
                              </Button>
                              <Button
                                size="small"
                                variant="text"
                                onClick={() => handleUpdateLayer(selectedLayer.id, { popupFields: [] })}
                              >
                                Clear (empty popup)
                              </Button>
                            </Stack>
                            <FormGroup sx={{ maxHeight: 200, overflow: 'auto', pl: 0.5 }}>
                              {selectedLayerSchema.map((c) => {
                                const set = new Set(selectedLayer.popupFields ?? []);
                                const checked = set.has(c.name);
                                return (
                                  <FormControlLabel
                                    key={c.name}
                                    control={(
                                      <Checkbox
                                        size="small"
                                        checked={checked}
                                        onChange={() => {
                                          const next = new Set(selectedLayer.popupFields ?? []);
                                          if (next.has(c.name)) next.delete(c.name);
                                          else next.add(c.name);
                                          handleUpdateLayer(selectedLayer.id, {
                                            popupFields: Array.from(next),
                                          });
                                        }}
                                      />
                                    )}
                                    label={(
                                      <Box>
                                        <Typography variant="body2" component="span">{c.name}</Typography>
                                        <Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 0.75 }}>
                                          {c.type}
                                        </Typography>
                                      </Box>
                                    )}
                                  />
                                );
                              })}
                            </FormGroup>
                          </Box>
                        )}
                      </Box>
                    </Stack>
                  )}
                </Box>

                <Divider />

                {/* Remove */}
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={() => handleRemoveLayer(selectedLayer.id)}
                  fullWidth
                >
                  Remove layer
                </Button>
              </Stack>
            </Box>
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default WebMapEditorDialog;
